import type { DocPage } from "../types";

export const page: DocPage = {
  slug: "activity",
  title: "Trade Logs and Journal",
  navLabel: "Activity",
  description:
    "Review orders and fills in Trade Logs, then connect each outcome to the thesis, decision, checkpoint, or post-trade note in Journal.",
  section: "portfolio-trading",
  published: "2026-08-03",
  updated: "2026-08-03",
  targets: ["Mavori trade logs", "Mavori trading journal", "review AI agent trading activity"],
  related: ["portfolio", "trading", "deployments", "automations"],
  blocks: [
    {
      type: "lead",
      text: "Portfolio has two historical views: Trade Logs records what happened to orders and fills, while Journal records the reasoning, checkpoints, and reviews around important decisions. Use them together to separate execution outcomes from decision quality.",
    },

    { type: "h2", text: "Review Trade Logs" },
    {
      type: "p",
      text: "Trade Logs is the order ledger across your Portfolio Accounts. Each row identifies the account, symbol, side, quantity, order type, status, fill details, source, and available thesis. Activity can come from the Agent, a Strategy Deployment, a scheduled task, a broker connection, or a trade you recorded on a Manual Account.",
    },
    {
      type: "p",
      text: "Search by symbol and use the status filters to focus on open, filled, canceled, or rejected activity. Open a linked conversation, Strategy, or Journal entry when you need the context behind a row. A partial fill remains open for the unfilled quantity; an uncertain status should be checked at the broker before another order is placed.",
    },

    { type: "h2", text: "Use Journal for the decision record" },
    {
      type: "p",
      text: "Journal holds theses, decisions, checkpoints, reviews, and post-mortems. Entries can preserve the reasoning that existed at the time, link to related orders, and show how a decision was later evaluated. You can inspect and edit the narrative record without changing the broker or position ledger.",
    },
    {
      type: "p",
      text: "Journal is different from Rules and Memory. Rules are standing instructions for the Agent; Memory is durable context retrieved when relevant; Journal is the chronological record of market decisions and their rationale. Manage Rules and Memory from [Account and settings](/docs/account).",
    },

    { type: "h2", text: "Run a focused review" },
    {
      type: "steps",
      items: [
        {
          title: "Start with the order",
          text: "Confirm the account, ticket, status, fills, fees, and source in Trade Logs.",
        },
        {
          title: "Open the linked reasoning",
          text: "Follow the thesis or Journal link and read what was known and intended at the time.",
        },
        {
          title: "Compare intent with outcome",
          text: "Separate execution quality, market movement, and whether the original process was followed.",
        },
        {
          title: "Record the lesson",
          text: "Add or update the Journal entry with a checkpoint, review, or reusable lesson.",
        },
      ],
    },

    { type: "h2", text: "Follow automated activity" },
    {
      type: "p",
      text: "Strategy Deployments and [scheduled tasks](/docs/automations) retain their own run or performance views, while Trade Logs and Journal provide the account-level history. Move between them when investigating an automated decision: the task or deployment explains the source, Trade Logs shows execution, and Journal preserves the rationale and review.",
    },
  ],
};
